import React, { memo, useMemo } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BorderedFlexBox from 'app/pages/loanApplication/Validation/restyled/LoanInfoModal/components/BorderedFlexBox';
import { NIL_OR_NAN_DEFAULT_PLACEHOLDER } from 'app/pages/loanApplication/Validation/restyled/LoanInfoModal/LoanInfoModal.constant';
import { getValueWithNilOrNaNPlaceholder } from 'app/pages/loanApplication/Validation/restyled/LoanInfoModal/LoanInfoModal.utils';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import { InfoIcon, WarningIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LoanOptionVendorType } from 'constants/company';
import { IconSize } from 'constants/coreDesignSystem';
import { OptimalBlueAdjustmentType } from 'constants/loanApplication';

import { ExpiredPriceWarningMessage } from '../../ProgramsAndPricing.constants';
import { ProgramsAndPricingTestIds } from '../../ProgramsAndPricing.testIds';
import { RatesheetStatus } from '../ProgramAccordion/ProgramAccordion.constants';

import RateTableRow from './components/RateTableRow';
import { DIGITS_REGEXP, FOR_DAYS_REGEXP, NO_INVESTOR_WARNINGS } from './ProgramDetails.constants';
import {
  additionalInfoListStyles,
  additionalTableStyles,
  expiredPriceWarningIconStyles,
  expiredPriceWarningStyles,
  firstHeadCellStyles,
  getProgramDetailsStyles,
  headCellStyles,
  headRowRatesStyles,
  investorWarningIconStyles,
  investorWarningsStyles,
  programDetailsWrapperStyles,
  ratesheetStateStyles,
  ratesTableStyles,
  secondColumnItemStyles,
  secondColumnStyles,
  totalAdjustmentsStyles,
} from './ProgramDetails.styles';
import { getDenialReasonParts, getPriceCompensationPoints } from './ProgramDetails.utils';

const ProgramDetails = ({
  programDetails,
  ratesheetData,
  ratesheetError,
  ratesheetStatus,
  selectedLoanProgramOption,
  currentLoanProgramOption,
  onRetryRatesheet,
  onSelectLoanProgramOption,
  pendingChanges,
  isProgramExpired = false,
}) => {
  const isRatesheetLoading = ratesheetStatus === RatesheetStatus.PENDING;
  const isRatesheetError = ratesheetStatus === RatesheetStatus.REJECTED;

  const ratesheetProgramDetails =
    ratesheetStatus === RatesheetStatus.FULFILLED ? _.get(ratesheetData, ['programs', 0]) : null;

  const currentProgramDetails = ratesheetProgramDetails ?? programDetails;

  const priceSheet = _.get(currentProgramDetails, ['rateCalculations', 'prices'], []).sort(
    (a, b) => a.noteRate - b.noteRate,
  );
  const vendorType = _.get(currentProgramDetails, ['program', 'vendorType']);

  const { allAdjustments, totalAdjustments } = useMemo(() => {
    const adjustments =
      currentProgramDetails?.adjustments || currentLoanProgramOption?.priceAdjustments;

    return {
      allAdjustments: _.get(adjustments, ['adjustments'], []),
      totalAdjustments: _.get(adjustments, ['coefficient']),
    };
  }, [currentLoanProgramOption, currentProgramDetails]);

  const filteredAdjustments =
    vendorType === LoanOptionVendorType.OPTIMAL_BLUE
      ? _.filter(allAdjustments, ({ type }) =>
          [OptimalBlueAdjustmentType.PRICE, OptimalBlueAdjustmentType.SRP].includes(type),
        )
      : allAdjustments;

  // Eligibility is the pricing engine's verdict in the loan-options payload; a ratesheet refresh only restates prices
  // and need not carry it. Reading the verdict off currentProgramDetails would let the block lapse the moment a
  // ratesheet loads, so take it from the prop — and only an explicit false denies, so a payload without an
  // eligibility object stays selectable (DEV-6521).
  const isProgramIneligible = _.get(programDetails, ['eligibility', 'isAllowed']) === false;

  const denialReasons = _.get(currentProgramDetails, ['eligibility', 'denialReasons'], []);
  const ineligibleReasons = _.get(currentProgramDetails, ['program', 'ineligibleReasons'], []);
  const additionalInfo = _.get(
    currentProgramDetails,
    ['program', 'additionalInfo', 'additionalInfoDescription'],
    null,
  );

  const additionalInfoContent = useMemo(() => {
    if (!_.size(additionalInfo)) {
      return NIL_OR_NAN_DEFAULT_PLACEHOLDER;
    }

    if (_.isArray(additionalInfo)) {
      return (
        <Box component="ol" sx={additionalInfoListStyles}>
          {additionalInfo.map(note => (
            <li key={note}>{note}</li>
          ))}
        </Box>
      );
    }

    return additionalInfo;
  }, [additionalInfo]);

  const programId = _.get(currentProgramDetails, 'program.programId');
  const compensationPoints = getPriceCompensationPoints(currentLoanProgramOption);

  const { buyPrice, finalBuyPrice } = currentLoanProgramOption;
  const investorWarnings = currentLoanProgramOption.investorWarnings ?? NO_INVESTOR_WARNINGS;
  const finalPrice = buyPrice + totalAdjustments;

  const columnDefs = useMemo(() => {
    return _.uniq(
      priceSheet.flatMap(dataRow => Object.keys(dataRow)).filter(key => FOR_DAYS_REGEXP.test(key)),
    )
      .map(value => {
        const term = parseInt(value.replace(DIGITS_REGEXP, ''));

        return {
          id: term,
          label: `${term} Days`,
        };
      })
      .sort((a, b) => a.id - b.id);
  }, [priceSheet]);

  if (_.size(ineligibleReasons)) {
    return (
      <TableContainer sx={additionalTableStyles}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ineligible Reasons</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {ineligibleReasons.map((reason, key) => (
              <TableRow key={key}>
                <TableCell>{reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Stack sx={programDetailsWrapperStyles}>
      <Divider />
      <Stack sx={getProgramDetailsStyles()}>
        <BorderedFlexBox>
          <Typography variant="medium16">Rates:</Typography>

          {isProgramExpired && (
            <Stack direction="row" sx={expiredPriceWarningStyles}>
              <Box component="span" sx={expiredPriceWarningIconStyles}>
                <WarningIcon size={IconSize[16]} />
              </Box>
              <Typography color="warning.main" variant="regular14">
                {ExpiredPriceWarningMessage}
              </Typography>
            </Stack>
          )}

          {investorWarnings.length > 0 && (
            <Stack
              data-testid={ProgramsAndPricingTestIds.PROGRAM_INVESTOR_WARNINGS}
              role="status"
              sx={investorWarningsStyles}
            >
              {investorWarnings.map(warning => (
                <Stack direction="row" key={warning} sx={expiredPriceWarningStyles}>
                  <Box component="span" sx={investorWarningIconStyles}>
                    <InfoIcon size={IconSize[16]} />
                  </Box>
                  <Typography color="text.secondary" variant="regular14">
                    {warning}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          {isRatesheetLoading && (
            <Stack alignItems="center" justifyContent="center" sx={ratesheetStateStyles}>
              <CircularProgress size={32} />
            </Stack>
          )}

          {isRatesheetError && (
            <Stack alignItems="center" spacing={2} sx={ratesheetStateStyles}>
              <Typography variant="regular14">
                {ratesheetError ?? 'Failed to load ratesheet'}
              </Typography>
              <Button
                data-testid={ProgramsAndPricingTestIds.RATES_RETRY_BUTTON}
                size="medium"
                onClick={onRetryRatesheet}
              >
                Retry
              </Button>
            </Stack>
          )}

          {!isRatesheetLoading && !isRatesheetError && !!priceSheet.length && (
            <TableContainer
              data-testid={ProgramsAndPricingTestIds.PROGRAM_RATES_TABLE}
              sx={ratesTableStyles}
            >
              <Table>
                <TableHead>
                  <TableRow sx={headRowRatesStyles}>
                    <TableCell align="right" sx={firstHeadCellStyles}>
                      Note Rate/ Payment
                    </TableCell>
                    {columnDefs.map(({ id, label }) => (
                      <TableCell align="right" key={id} sx={headCellStyles}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {priceSheet.map((rowData, index) => (
                    <RateTableRow
                      columnDefs={columnDefs}
                      isProgramExpired={isProgramExpired}
                      isProgramIneligible={isProgramIneligible}
                      key={`${index}-${rowData.noteRate}`}
                      pendingChanges={pendingChanges}
                      programDetails={currentProgramDetails}
                      programId={programId}
                      rowData={rowData}
                      selectedLoanProgramOption={selectedLoanProgramOption}
                      onSelectLoanProgramOption={onSelectLoanProgramOption}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!isRatesheetLoading && !isRatesheetError && !priceSheet.length && (
            <NoAvailableMessage message="There is no data available for this section" />
          )}
        </BorderedFlexBox>

        <Stack sx={secondColumnStyles}>
          <BorderedFlexBox customStyles={secondColumnItemStyles}>
            <Typography variant="medium20">Guidelines</Typography>
            <TableContainer
              data-testid={ProgramsAndPricingTestIds.PROGRAM_ELIGIBILITY_TABLE}
              sx={additionalTableStyles}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Eligibility</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {denialReasons.length ? (
                    denialReasons.map(({ criteriaId, ...reason }) => {
                      const { label, text } = getDenialReasonParts(reason);

                      return (
                        <TableRow key={criteriaId}>
                          <TableCell>
                            {!!label && <Typography variant="bold14">{label}</Typography>}
                            {/* denialReason is prose — getValueWithNilOrNaNPlaceholder runs its input through
                                formatNumberToFixed3Decimals, which rendered every reason as NaN (DEV-6519). */}
                            {text}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell>{NIL_OR_NAN_DEFAULT_PLACEHOLDER}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer
              data-testid={ProgramsAndPricingTestIds.PROGRAM_ADDITIONAL_INFO_TABLE}
              sx={additionalTableStyles}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Additional Info</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell className="max-w-px">{additionalInfoContent}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </BorderedFlexBox>

          <BorderedFlexBox customStyles={secondColumnItemStyles}>
            <Typography variant="medium20">Adjustments</Typography>

            <TableContainer
              data-testid={ProgramsAndPricingTestIds.PROGRAM_ADJUSTMENTS_TABLE}
              sx={additionalTableStyles}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Base Price</TableCell>
                    <TableCell align="right">{getValueWithNilOrNaNPlaceholder(buyPrice)}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredAdjustments.map(({ criteriaId, adjustmentName, adjustment }, index) => (
                    <TableRow key={criteriaId ?? `${adjustmentName}-${index}`}>
                      <TableCell>{adjustmentName}</TableCell>
                      <TableCell align="right">{adjustment}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" sx={totalAdjustmentsStyles}>
              <Typography variant="regular14">Total Adjustments</Typography>
              <Typography className="amount" variant="regular14">
                {getValueWithNilOrNaNPlaceholder(totalAdjustments)}
              </Typography>
            </Stack>

            <TableContainer
              data-testid={ProgramsAndPricingTestIds.PROGRAM_FINAL_PRICES_TABLE}
              sx={additionalTableStyles}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Final Price</TableCell>
                    <TableCell align="right">
                      {getValueWithNilOrNaNPlaceholder(finalPrice)}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell>Compensation</TableCell>
                    <TableCell align="right">
                      {getValueWithNilOrNaNPlaceholder(compensationPoints)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="total">Final Price after Comp</TableCell>
                    <TableCell align="right" className="total">
                      {getValueWithNilOrNaNPlaceholder(finalBuyPrice)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </BorderedFlexBox>
        </Stack>
      </Stack>
    </Stack>
  );
};

ProgramDetails.propTypes = {
  programDetails: PropTypes.object.isRequired,
  currentLoanProgramOption: PropTypes.object.isRequired,
  onSelectLoanProgramOption: PropTypes.func.isRequired,
  selectedLoanProgramOption: PropTypes.object,
  pendingChanges: PropTypes.object,
  isProgramExpired: PropTypes.bool,
  ratesheetData: PropTypes.object,
  ratesheetError: PropTypes.string,
  ratesheetStatus: PropTypes.oneOf(Object.values(RatesheetStatus)),
  onRetryRatesheet: PropTypes.func,
};

export default memo(ProgramDetails);
